import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Divider, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Fade
} from '@mui/material';
import { 
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  CompareArrows as CompareArrowsIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import PageHeader from '../components/common/PageHeader';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { DataTable, ColumnType } from '../types/table.types';
import { TableData, TableDataHistory } from '../types/tableData.types';

export default function TableDataHistoryPage() {
  const { id, dataId } = useParams<{ id: string; dataId: string }>();
  const navigate = useNavigate();
  
  const [table, setTable] = useState<DataTable | null>(null);
  const [data, setData] = useState<TableData | null>(null);
  const [history, setHistory] = useState<TableDataHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (id && dataId) {
      fetchData(id, dataId);
    }
  }, [id, dataId]);
  
  const fetchData = async (tableId: string, dataId: string) => {
    try {
      setLoading(true);
      
      const tableData = await TableService.getTableById(tableId);
      setTable(tableData);
      
      const recordData = await TableDataService.getTableDataById(tableId, dataId);
      setData(recordData);
      
      const historyData = await TableDataService.getTableDataHistory(tableId, dataId);
      setHistory(historyData);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate(`/tables/${id}/data/${dataId}`);
  };
  
  const handleBackToTable = () => {
    navigate(`/tables/${id}`);
  };
  
  const formatValue = (value: any, columnType: ColumnType): React.ReactNode => {
    if (value === null || value === undefined) {
      return <Typography color="text.secondary">Нет данных</Typography>;
    }
    
    switch (columnType) {
      case ColumnType.TEXT:
        return <Typography>{value}</Typography>;
      case ColumnType.NUMBER:
        return <Typography>{value}</Typography>;
      case ColumnType.DATE:
        return <Typography>{dayjs(value).format('DD.MM.YYYY')}</Typography>;
      case ColumnType.BOOLEAN:
        return <Chip 
          label={value ? 'Да' : 'Нет'} 
          color={value ? 'success' : 'default'} 
          size="small" 
          sx={{ fontWeight: 600 }}
        />;
      case ColumnType.DROPDOWN:
        return <Chip label={value} size="small" sx={{ fontWeight: 600 }} />;
      default:
        return <Typography>{String(value)}</Typography>;
    }
  };
  
  const getChangedFields = (oldData: Record<string, any>, newData: Record<string, any>): string[] => {
    const changedFields: string[] = [];
    
    Object.keys(newData).forEach(key => {
      if (JSON.stringify(oldData[key]) !== JSON.stringify(newData[key])) {
        changedFields.push(key);
      }
    });
    
    Object.keys(oldData).forEach(key => {
      if (newData[key] === undefined) {
        changedFields.push(key);
      }
    });
    
    return changedFields;
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!table || !data) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Данные не найдены или у вас нет доступа к ним
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/tables')}
          sx={{ mt: 2 }}
        >
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <PageHeader 
        title="История изменений"
        subtitle={`Таблица: ${table.name}`}
        breadcrumbs={[
          { title: 'Таблицы', href: '/tables' },
          { title: table.name, href: `/tables/${id}` },
          { title: 'Запись', href: `/tables/${id}/data/${dataId}` },
          { title: 'История изменений' },
        ]}
      />
      
      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Fade>
      )}
      
      {/* Текущая версия */}
      <Fade in timeout={600}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)',
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Текущая версия
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {dayjs(data.updatedAt).format('DD.MM.YYYY HH:mm')}
                    </Typography>
                    {data.updatedBy && (
                      <>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {data.updatedBy.fullName}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                К записи
              </Button>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Текущие значения полей</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                  {table.columns.map((column) => (
                    <Box 
                      key={column.id} 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(76, 175, 80, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                        {column.name}
                      </Typography>
                      {formatValue(data.data[column.name], column.type)}
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      </Fade>
      
      {/* История изменений */}
      <Fade in timeout={800}>
        <Paper 
          sx={{ 
            p: 3,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 48, height: 48 }}>
              <HistoryIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              История изменений
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          {history.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                История изменений отсутствует
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Вертикальная линия */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 24,
                  top: 32,
                  bottom: 32,
                  width: 2,
                  bgcolor: 'rgba(102, 126, 234, 0.2)',
                }}
              />
              
              <List sx={{ p: 0 }}>
                {history.map((historyItem, index) => {
                  const nextItem = index === 0 ? data : history[index - 1];
                  const changedFields = getChangedFields(historyItem.data, nextItem.data);
                  
                  return (
                    <ListItem 
                      key={historyItem.id}
                      alignItems="flex-start"
                      sx={{ 
                        pb: 4,
                        position: 'relative',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: '#667eea',
                            width: 48,
                            height: 48,
                            border: '4px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          }}
                        >
                          <HistoryIcon />
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        sx={{ ml: 2 }}
                        primary={
                          <Paper
                            elevation={2}
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                              }
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                              Версия от {dayjs(historyItem.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                              {historyItem.user && (
                                <Chip
                                  icon={<PersonIcon />}
                                  label={historyItem.user.fullName}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {historyItem.changeDescription && (
                                <Chip
                                  label={historyItem.changeDescription}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Измененные поля: {changedFields.length > 0 ? (
                                <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap', ml: 1 }}>
                                  {changedFields.map(field => (
                                    <Chip 
                                      key={field} 
                                      label={field} 
                                      size="small" 
                                      sx={{ 
                                        height: 20,
                                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                                        color: '#667eea',
                                        fontWeight: 600
                                      }}
                                    />
                                  ))}
                                </Box>
                              ) : 'Нет изменений'}
                            </Typography>
                            
                            <Accordion sx={{ boxShadow: 'none', bgcolor: 'rgba(0,0,0,0.02)' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Подробнее</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                                  {table.columns.map((column) => (
                                    <Box key={column.id}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {column.name}
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        {formatValue(historyItem.data[column.name], column.type)}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                            
                            {index < history.length - 1 && (
                              <Accordion sx={{ boxShadow: 'none', bgcolor: 'rgba(0,0,0,0.02)', mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CompareArrowsIcon fontSize="small" sx={{ color: '#667eea' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Сравнение с предыдущей версией
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {changedFields.map(field => {
                                      const column = table.columns.find(col => col.name === field);
                                      if (!column) return null;
                                      
                                      return (
                                        <Box 
                                          key={field}
                                          sx={{
                                            p: 2,
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            border: '1px solid rgba(0,0,0,0.08)',
                                          }}
                                        >
                                          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                                            {field}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>
                                                Было:
                                              </Typography>
                                              <Box sx={{ 
                                                p: 1.5, 
                                                bgcolor: 'rgba(244, 67, 54, 0.05)', 
                                                borderRadius: 1,
                                                border: '1px solid rgba(244, 67, 54, 0.2)',
                                                mt: 0.5
                                              }}>
                                                {formatValue(historyItem.data[field], column.type)}
                                              </Box>
                                            </Box>
                                            <CompareArrowsIcon sx={{ color: '#667eea' }} />
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                                                Стало:
                                              </Typography>
                                              <Box sx={{ 
                                                p: 1.5, 
                                                bgcolor: 'rgba(76, 175, 80, 0.05)', 
                                                borderRadius: 1,
                                                border: '1px solid rgba(76, 175, 80, 0.2)',
                                                mt: 0.5
                                              }}>
                                                {formatValue(history[index - 1].data[field], column.type)}
                                              </Box>
                                            </Box>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Paper>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Paper>
      </Fade>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToTable}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          К таблице
        </Button>
        
        <Button
          variant="contained"
          onClick={handleBack}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            }
          }}
        >
          К записи
        </Button>
      </Box>
    </Box>
  );
}